import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock, BarChart3, Globe, Shield, Zap, Star,
  ChevronRight, CheckCircle2, AlertTriangle, Package, MapPin,
  ArrowRight, Layers, ShieldCheck, TrendingUp, BadgeCheck, Coins,
  Info, EyeOff,
} from 'lucide-react';
import BookmarkButton from '@/components/BookmarkButton';
import PlatformLogo from '@/components/PlatformLogo';
import { Button } from '@/components/ui/button';
import { getActivityBadgeStyle, getSafetyBadgeStyle, getSkillBadgeStyle } from '@/lib/badgeUtils';
import { getLocalizedEarnings, isCountryInList, convertCurrencySymbol, getCurrencyForCountry } from '@/lib/countryCodeUtils';
import { useCompare } from '@/context/CompareContext';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from 'sonner';

/**
 * Premium unified OpportunityCard with multiple variant states.
 *
 * @param {object}   opp              Opportunity data from API
 * @param {string}   variant          "standard" | "recommended" | "best-match" | "personalized" | "pathway"
 * @param {number}   matchScore       0-100 (from recommendation engine)
 * @param {string[]} matchReasons     Why this matches
 * @param {string[]} matchConflicts   Conflict warnings
 * @param {number}   matchConfidence  0-1
 * @param {string}   matchCategory    e.g. "Skill Match"
 * @param {string}   currency         Target currency code
 * @param {string}   userCountry      User's selected country
 * @param {boolean}  isLoggedIn
 * @param {boolean}  showBookmark     Show bookmark button (default true)
 * @param {function} onUnsave         If set, shows unsave button instead of bookmark
 * @param {object}   payGuidance      User-aware pay guidance from batch endpoint
 * @param {string}   className
 */
export default function OpportunityCard({
  opp,
  variant = 'standard',
  matchScore,
  matchReasons = [],
  matchConflicts = [],
  matchConfidence,
  matchCategory,
  currency = 'USD',
  userCountry = '',
  isLoggedIn = false,
  showBookmark = true,
  onUnsave,
  onHide,
  onUnhide,
  payGuidance,
  className = '',
}) {
  const navigate = useNavigate();
  const [showReasons, setShowReasons] = useState(false);
  const [showPayInsights, setShowPayInsights] = useState(false);
  const payInsightRef = useRef(null);
  const { addToCompare, removeFromCompare: removeCompare, isInCompare } = useCompare();
  const t = useTranslation();
  const inCompare = isInCompare(opp.opportunity_id);

  const isBestMatch = variant === 'best-match';
  const isRecommended = variant === 'recommended' || variant === 'personalized' || isBestMatch;
  const isPersonalized = variant === 'personalized';
  const isPathway = variant === 'pathway';
  const isFeatured = opp.is_featured;
  // Active-sponsored gate (Feb 2026): the bare ``is_featured`` flag is not
  // enough to render a public "Sponsored" badge — a stale legacy flag could
  // mislead users and breach AdSense / FTC / EU ad-disclosure rules. Only
  // show the badge when there is real evidence of an active paid placement:
  //   - ``stripe_sponsored`` truthy, OR
  //   - ``sponsored_source`` populated, OR
  //   - ``sponsored_until`` is a future timestamp.
  const _sponsoredUntil = opp.sponsored_until ? new Date(opp.sponsored_until) : null;
  const _sponsoredUntilFuture = !!(_sponsoredUntil && !isNaN(_sponsoredUntil.getTime()) && _sponsoredUntil > new Date());
  const isActiveSponsored = !!isFeatured && (
    !!opp.stripe_sponsored || !!opp.sponsored_source || _sponsoredUntilFuture
  );
  const hasMatchData = matchScore != null && matchScore > 0;
  const isArchived = opp.archived === true || opp.status === 'unavailable';
  const isBroken = opp.link_status_overall === 'broken';

  // Hard visibility rule: never render cards with zero verified destinations
  const hasVerifiedDest = opp.has_verified_destination !== false && (
    opp.website || opp.official_website_url || opp.ios_app_url || opp.android_app_url
  );
  if (!hasVerifiedDest) return null;

  // Region availability check
  const countries = opp.countries || [];
  const restricted = opp.restricted_countries || [];
  const _globalTerms = ['global', 'worldwide', 'international'];
  const isGlobal = countries.length === 0 || countries.some(c => _globalTerms.includes(c.toLowerCase()));
  const isRestricted = userCountry && isCountryInList(userCountry, restricted);
  const isAvailableInRegion = isGlobal || !userCountry || isCountryInList(userCountry, countries);
  const regionUnavailable = isRestricted || (!isGlobal && userCountry && !isAvailableInRegion);

  // Availability audit status
  const availStatus = opp.availability_status; // confirmed_global | confirmed_local | potentially_global

  // Listing type classification
  const listingType = opp.listing_type; // income_source | requirement | optimizer | tool | service | education
  const isNonIncome = listingType && listingType !== 'income_source';
  const listingTypeLabel = {
    requirement: 'Required for this opportunity',
    optimizer: 'Optimiser / Service',
    tool: 'Tool',
    service: 'Service',
    education: 'Education',
  }[listingType] || null;

  // Localized earnings logic
  const localizedData = getLocalizedEarnings(opp, userCountry);
  const userCurrency = getCurrencyForCountry(userCountry) || currency;
  // Backend-supplied approximate FX-converted earnings (used for regions
  // without an editor-verified localized_earnings entry). Only honoured when
  // we have no local override of our own.
  const hasApproxFromBackend = !localizedData && !!opp.approx_earnings && opp.is_approx_earnings === true;
  const displayEarnings = localizedData
    ? localizedData.range
    : (hasApproxFromBackend
        ? opp.approx_earnings
        : convertCurrencySymbol(opp.earnings_range, userCurrency));
  const hasLocalizedEarnings = !!localizedData;
  const confidenceLevel = localizedData?.confidence_level || null;
  const incomeFreq = opp.income_frequency || '';
  const isPricingOutdated = opp.pricing_flagged_outdated === true;

  const safetyStyle = getSafetyBadgeStyle(opp.safety_grade);

  // Top bar gradient based on variant
  const topBarClass =
    isBestMatch ? 'h-1 bg-gradient-to-r from-teal-400 via-teal-500 to-cyan-400' :
    isFeatured ? 'h-1 bg-gradient-to-r from-amber-400 to-orange-400' :
    isRecommended ? 'h-0.5 bg-gradient-to-r from-blue-400 to-indigo-400' :
    null;

  // Card border/shadow
  const cardBorder =
    isArchived ? 'border-red-200/60 opacity-70' :
    isBestMatch ? 'border-teal-200/60 shadow-[0_0_0_1px_rgba(20,184,166,0.08),0_4px_20px_rgba(20,184,166,0.06)]' :
    isFeatured ? 'border-amber-200/60 shadow-[0_2px_12px_rgba(245,158,11,0.06)]' :
    regionUnavailable ? 'border-slate-200/60 opacity-60' :
    'border-slate-200/60 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_1px_2px_rgba(15,23,42,0.02)]';

  const hoverClass = (regionUnavailable || isArchived)
    ? ''
    : 'hover:shadow-[0_12px_36px_rgba(15,23,42,0.08),0_4px_8px_rgba(15,23,42,0.03)] hover:border-slate-300/80 hover:-translate-y-0.5';

  return (
    <div
      className={`group bg-white rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden ${cardBorder} ${hoverClass} ${className}`}
      data-testid={`opportunity-card-${opp.opportunity_id}`}
      onClick={() => {
        sessionStorage.setItem('browse_scroll', window.scrollY.toString());
        navigate(`/opportunity/${opp.opportunity_id}`);
      }}
      role="article"
      aria-label={opp.name}
    >
      {/* Archived/Unavailable banner */}
      {isArchived && (
        <div className="bg-red-50 border-b border-red-200/60 px-4 py-2 flex items-center gap-2" data-testid={`archived-banner-${opp.opportunity_id}`}>
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <span className="text-[11px] font-semibold text-red-600">Platform unavailable or discontinued</span>
        </div>
      )}

      {/* === TOP BAR === */}
      {topBarClass && <div className={topBarClass} />}

      {/* === Match score bar (recommended/best-match) === */}
      {isRecommended && hasMatchData && (
        <div className="h-1 bg-slate-100">
          <div
            className={`h-full transition-all duration-700 ${
              matchScore >= 80 ? 'bg-gradient-to-r from-emerald-500 to-green-500' :
              matchScore >= 60 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
              'bg-gradient-to-r from-slate-400 to-slate-500'
            }`}
            style={{ width: `${Math.min(matchScore, 100)}%` }}
          />
        </div>
      )}

      <div className="p-5">

        {/* === TOP ROW: Badges === */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {/* P1: Match badge — always visible */}
            {isBestMatch && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 border border-emerald-200/60" data-testid={`best-match-badge-${opp.opportunity_id}`}>
                <Star className="h-2.5 w-2.5 fill-emerald-500 text-emerald-500" /> Best Match
              </span>
            )}
            {!isBestMatch && isRecommended && hasMatchData && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                matchScore >= 80 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/50' :
                matchScore >= 60 ? 'bg-blue-50 text-blue-700 border border-blue-200/50' :
                'bg-slate-50 text-slate-600 border border-slate-200/50'
              }`} data-testid={`match-score-${opp.opportunity_id}`}>
                <Zap className="h-2.5 w-2.5" />
                {matchScore}%
              </span>
            )}
            {/* P1: Sponsored — only render when there is REAL evidence of an
                active paid placement (Feb 2026). A bare ``is_featured`` flag
                without ``stripe_sponsored`` / ``sponsored_source`` /
                future ``sponsored_until`` is treated as a stale legacy flag
                and the badge is suppressed to stay AdSense/FTC compliant. */}
            {isActiveSponsored && !isBestMatch && (
              <span
                className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200/50 cursor-help"
                title="Sponsored placement — paid by the listing owner. Ranking remains capped to keep results fair."
                data-testid={`sponsored-badge-${opp.opportunity_id}`}
              >
                <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" /> Sponsored
              </span>
            )}
            {/* P2: Category — always visible */}
            <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-50 text-slate-500 border border-slate-100 truncate max-w-[100px] sm:max-w-[120px]">
              {opp.category}
            </span>
            {/* P3: Income type — always visible */}
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${getActivityBadgeStyle(opp.passive_active)}`}>
              {opp.passive_active}
            </span>
            {/* P4+: Tier, Verified, Trending — hidden on mobile, visible sm+ */}
            {opp.listing_tier === 'premium' && (
              <span className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200/50" data-testid={`tier-premium-${opp.opportunity_id}`}>
                <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> Premium
              </span>
            )}
            {opp.listing_tier === 'featured' && !isFeatured && (
              <span className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-blue-50 text-blue-600 border border-blue-200/50" data-testid={`tier-featured-${opp.opportunity_id}`}>
                Featured
              </span>
            )}
            {opp.safety_score_source === 'manual' && opp.safety_score >= 80 && (
              <span className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200/50" data-testid={`verified-badge-${opp.opportunity_id}`}>
                <BadgeCheck className="h-2.5 w-2.5" /> Verified
              </span>
            )}
            {(opp.views_count > 100 || opp.clicks_count > 20) && (
              <span className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200/50" data-testid={`trending-badge-${opp.opportunity_id}`}>
                <TrendingUp className="h-2.5 w-2.5" /> Trending
              </span>
            )}
            {/* Listing type badge for non-income listings */}
            {isNonIncome && (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-50 text-violet-600 border border-violet-200/50" data-testid={`listing-type-${opp.opportunity_id}`}>
                {listingTypeLabel}
              </span>
            )}
            {/* Seasonal badge */}
            {opp.seasonal_badge && (
              <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                opp.display_mode === 'show_if_filtered'
                  ? 'bg-slate-100 text-slate-500 border border-slate-200/50'
                  : opp.listing_status === 'seasonal'
                    ? 'bg-orange-50 text-orange-600 border border-orange-200/50'
                    : 'bg-teal-50 text-teal-600 border border-teal-200/50'
              }`} data-testid={`seasonal-badge-${opp.opportunity_id}`}>
                {opp.seasonal_badge}
              </span>
            )}
          </div>

          {/* Trust score with two-tier badge + tooltip */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {(() => {
              const score = opp.safety_score;
              const source = opp.safety_score_source;
              const confidence = opp.safety_score_confidence;
              const evidence = opp.safety_score_evidence;
              const isLowConfidence = source === 'ai_estimated' && (confidence != null && confidence < 0.4);

              // Shared tooltip explainer — matches Feb 2026 E-E-A-T copy.
              const baseExplainer = 'Scores consider legitimacy, realistic earnings, time commitment, payout clarity and red flags.';

              if (score == null || score <= 0) {
                // No score yet — soft, non-alarming fallback.
                return (
                  <div
                    className="relative flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium cursor-help group/trust bg-slate-50 text-slate-500 border border-slate-200/60"
                    data-testid={`trust-score-${opp.opportunity_id}`}
                    aria-label="Editorially listed — trust score pending"
                  >
                    <Shield className="h-2.5 w-2.5" />
                    <span>Editorially listed</span>
                    <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover/trust:block z-30 pointer-events-none">
                      <div className="bg-slate-900 text-white text-[10px] font-medium px-2.5 py-1.5 rounded-lg max-w-[220px] shadow-lg leading-relaxed">
                        Listed using HustleHub&rsquo;s editorial criteria. Trust score is still being established.
                        <div className="text-slate-400 mt-0.5">{baseExplainer}</div>
                        <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (isLowConfidence) {
                // AI scored but low confidence — show score with softer label.
                return (
                  <div
                    className="relative flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium cursor-help group/trust bg-slate-50 text-slate-500 border border-slate-200/60"
                    data-testid={`trust-score-${opp.opportunity_id}`}
                    aria-label={`Trust score ${score} out of 100 — new or unverified listing`}
                  >
                    <Shield className="h-2.5 w-2.5" />
                    <span>{score}/100</span>
                    <span className="font-medium opacity-70">·</span>
                    <span className="font-medium text-[9px] uppercase tracking-wide opacity-80">New / unverified</span>
                    <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover/trust:block z-30 pointer-events-none">
                      <div className="bg-slate-900 text-white text-[10px] font-medium px-2.5 py-1.5 rounded-lg max-w-[240px] shadow-lg leading-relaxed">
                        Trust score {score}/100. New listing &mdash; full editorial review still in progress.
                        <div className="text-slate-400 mt-0.5">{baseExplainer}</div>
                        <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900" />
                      </div>
                    </div>
                  </div>
                );
              }

              // Source flags
              const isManual = source === 'manual';
              const isLegacy = source === 'legacy';
              const isAI = source === 'ai_estimated';

              // Tier label — per Feb 2026 E-E-A-T spec.
              // Avoids "Verified safe" / "Guaranteed" wording.
              const tierLabel =
                isManual && score >= 80 ? 'Editorially reviewed' :
                score >= 80 ? 'High trust' :
                score >= 50 ? 'Moderate trust' :
                'Review carefully';

              const colorClass = score >= 80
                ? (isManual ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60' : isLegacy ? 'bg-emerald-50/70 text-emerald-600 border border-emerald-200/40' : 'bg-teal-50 text-teal-600 border border-teal-200/50')
                : score >= 50
                ? (isManual ? 'bg-amber-50 text-amber-700 border border-amber-200/60' : 'bg-amber-50/70 text-amber-600 border border-amber-200/40')
                : 'bg-amber-50 text-amber-700 border border-amber-200/60';

              const Icon = isManual ? ShieldCheck : isAI ? Shield : Shield;
              const tooltipSource = isManual
                ? 'Reviewed by our editorial team.'
                : isLegacy
                ? 'Generated from earlier data and refreshed as new signals come in.'
                : "Generated using HustleHub's editorial criteria and updated as data improves.";
              const tooltipEvidence = evidence && evidence !== 'Score imported from initial data seeding.' ? ` ${evidence}` : '';

              return (
                <div
                  className={`relative flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold cursor-help group/trust ${colorClass}`}
                  data-testid={`trust-score-${opp.opportunity_id}`}
                  aria-label={`Trust score ${score} out of 100 — ${tierLabel}`}
                >
                  <Icon className="h-2.5 w-2.5" />
                  <span>{score}/100</span>
                  <span className="font-medium opacity-70">·</span>
                  <span className="font-medium text-[9px] uppercase tracking-wide opacity-80">{tierLabel}</span>
                  {/* Tooltip */}
                  <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover/trust:block z-30 pointer-events-none">
                    <div className="bg-slate-900 text-white text-[10px] font-medium px-2.5 py-1.5 rounded-lg max-w-[260px] shadow-lg leading-relaxed">
                      Trust score {score}/100{tooltipEvidence && <><br />{tooltipEvidence}</>}
                      <div className="text-slate-400 mt-0.5">{baseExplainer} {tooltipSource}</div>
                      <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900" />
                    </div>
                  </div>
                </div>
              );
            })()}
            {opp.safety_grade && (
              <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold flex-shrink-0 ${safetyStyle.bg} ${safetyStyle.text}`} data-testid={`safety-mini-${opp.opportunity_id}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${safetyStyle.dot}`} />
                {opp.safety_grade}
              </div>
            )}
          </div>
        </div>

        {/* === TITLE === */}
        <div className="flex items-start gap-2.5 mb-1.5">
          <PlatformLogo website={opp.website} name={opp.name} size="md" logoSource={opp.logo_source} logoInitials={opp.logo_text_initials} logoColor={opp.logo_text_color} />
          <h3 className="text-[15px] font-semibold text-slate-900 group-hover:text-teal-600 transition-colors duration-200 leading-snug line-clamp-2 pt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {opp.name}
          </h3>
        </div>

        {/* === DESCRIPTION === */}
        <p className="text-[13px] text-slate-500 leading-relaxed mb-3 line-clamp-2">{opp.description}</p>

        {/* === MATCH INSIGHT (logged-in personalized) === */}
        {isPersonalized && matchReasons.length > 0 && (
          <div className="mb-3 space-y-1" data-testid={`match-reasons-${opp.opportunity_id}`}>
            {matchReasons.slice(0, 2).map((reason, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[12px] text-slate-600">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span className="line-clamp-1">{reason}</span>
              </div>
            ))}
          </div>
        )}
        {isPersonalized && matchConflicts.length > 0 && (
          <div className="mb-3 space-y-1" data-testid={`match-conflicts-${opp.opportunity_id}`}>
            {matchConflicts.slice(0, 1).map((conflict, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[12px] text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="line-clamp-1">{conflict}</span>
              </div>
            ))}
          </div>
        )}

        {/* === KEY METADATA GRID === */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2 mb-3 py-3 border-t border-b border-slate-100/80">
          {/* Earnings — bold + prominent */}
          <div className="flex items-center gap-1.5 min-w-0 col-span-2 sm:col-span-1">
            {hasApproxFromBackend && (
              <span
                className="shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-slate-100 text-slate-500 cursor-help"
                title="Approximate conversion from USD. Actual rates vary by platform, location, demand, experience, and exchange-rate changes."
                data-testid={`approx-earnings-badge-${opp.opportunity_id}`}
              >
                Approx.
              </span>
            )}
            <span className="text-[13px] font-bold text-slate-900 truncate" title={opp.pay_display_label || (hasLocalizedEarnings ? `${confidenceLevel === 'verified' ? 'Verified' : confidenceLevel === 'estimated' ? 'Estimated' : ''} ${localizedData.currency} data` : (hasApproxFromBackend ? `Approximate ${opp.approx_earnings_currency} (static FX from USD). Actual rates vary by platform, location, demand, experience, and exchange rates.` : 'Base earnings'))} data-testid={`earnings-${opp.opportunity_id}`}>
              {displayEarnings}
            </span>
            {opp.pay_verification_status && (
              <span className={`shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                opp.pay_verification_status === 'verified' ? 'bg-green-50 text-green-600' :
                opp.pay_verification_status === 'unclear' ? 'bg-amber-50 text-amber-600' :
                'bg-slate-50 text-slate-400'
              }`} data-testid={`pay-badge-${opp.opportunity_id}`}>
                {opp.pay_verification_status === 'verified' ? 'Verified' :
                 opp.pay_verification_status === 'unclear' ? 'Varies' : 'Est.'}
              </span>
            )}
            {incomeFreq && <span className="text-[10px] text-slate-400 hidden sm:inline">/{incomeFreq}</span>}
            {/* Pay Insights trigger */}
            {payGuidance && (payGuidance.legal_floor_reference || payGuidance.caveats?.length > 0) && (
              <button onClick={(e) => { e.stopPropagation(); setShowPayInsights(!showPayInsights); }}
                className="shrink-0 ml-0.5 p-0.5 rounded-full hover:bg-teal-50 transition-colors group/info"
                data-testid={`pay-insights-trigger-${opp.opportunity_id}`} title="Pay insights for your profile">
                <Info className="h-3.5 w-3.5 text-teal-500 group-hover/info:text-teal-600" />
              </button>
            )}
          </div>
          {/* Pay Insights Panel (expandable) */}
          {showPayInsights && payGuidance && (
            <div ref={payInsightRef} className="col-span-2 sm:col-span-3 -mt-1 mb-1 p-2.5 bg-gradient-to-r from-teal-50/80 to-blue-50/60 border border-teal-100/80 rounded-lg animate-in fade-in-0 slide-in-from-top-1 duration-200"
              data-testid={`pay-insights-panel-${opp.opportunity_id}`}>
              <div className="space-y-1.5">
                {payGuidance.employer_pay && (
                  <div className="flex items-start gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-[11px] text-slate-700">Employer listed pay: <span className="font-semibold">{payGuidance.employer_pay}</span></span>
                  </div>
                )}
                {payGuidance.estimated_pay && !payGuidance.employer_pay && (
                  <div className="flex items-start gap-1.5">
                    <BarChart3 className="h-3 w-3 text-blue-500 shrink-0 mt-0.5" />
                    <span className="text-[11px] text-slate-700">Estimated pay: <span className="font-semibold">{payGuidance.estimated_pay}</span></span>
                  </div>
                )}
                {payGuidance.legal_floor_reference && (
                  <div className="flex items-start gap-1.5">
                    <Shield className="h-3 w-3 text-indigo-500 shrink-0 mt-0.5" />
                    <span className="text-[11px] text-slate-700">{payGuidance.legal_floor_reference.label}: <span className="font-semibold">{payGuidance.legal_floor_reference.display}</span></span>
                  </div>
                )}
                {payGuidance.caveats?.map((caveat, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <Info className="h-3 w-3 text-slate-400 shrink-0 mt-0.5" />
                    <span className="text-[11px] text-slate-500">{caveat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Time */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Clock className="h-3.5 w-3.5 text-blue-400 shrink-0" />
            <span className="text-[12px] text-slate-500 truncate">{opp.time_required}</span>
          </div>
          {/* Difficulty */}
          <div className="flex items-center gap-1.5 min-w-0">
            <BarChart3 className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
            <span className={`text-[12px] font-medium truncate ${opp.skill_level === 'Beginner' ? 'text-teal-600' : 'text-slate-500'}`}>
              {opp.skill_level || 'Any'}
            </span>
          </div>
          {/* Location */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Globe className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span className="text-[12px] text-slate-500 truncate">
              {isGlobal ? t('card.global') : `${countries.length} ${countries.length !== 1 ? t('card.regions') : t('card.region')}`}
            </span>
          </div>
          {/* Safety */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Shield className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span className={`text-[12px] font-medium ${safetyStyle.text}`}>
              {opp.safety_grade ? `${safetyStyle.label}` : t('card.unrated')}
            </span>
          </div>
          {/* Assets or Platform */}
          <div className="flex items-center gap-1.5 min-w-0">
            {opp.assets_required && opp.assets_required.length > 0 ? (
              <>
                <Package className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="text-[12px] text-slate-500 truncate">{opp.assets_required.slice(0, 2).join(', ')}</span>
              </>
            ) : (
              <>
                <Layers className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="text-[12px] text-slate-500 truncate">{opp.platform_type || 'Online'}</span>
              </>
            )}
          </div>
        </div>

        {/* Setup cost indicator */}
        {opp.assets_required && opp.assets_required.length > 0 ? (
          <div className="flex items-center gap-1.5 mb-1 -mt-1" data-testid={`setup-cost-${opp.opportunity_id}`}>
            <Coins className="h-3 w-3 text-amber-500" />
            <span className="text-[11px] text-amber-600 font-medium">Setup cost: Assets required</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mb-1 -mt-1" data-testid={`setup-cost-${opp.opportunity_id}`}>
            <Coins className="h-3 w-3 text-emerald-500" />
            <span className="text-[11px] text-emerald-600 font-medium">Free to start</span>
          </div>
        )}

        {/* === SECONDARY META: Region + Availability + Beginner + Localized === */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {opp.skill_level === 'Beginner' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-50 text-teal-600 border border-teal-100" data-testid="beginner-badge">
              <CheckCircle2 className="h-2.5 w-2.5" /> {t('card.beginnerFriendly')}
            </span>
          )}
          {/* Availability badge based on audit status */}
          {availStatus === 'confirmed_global' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-100" data-testid="avail-worldwide">
              <Globe className="h-2.5 w-2.5" /> Available Worldwide
            </span>
          )}
          {availStatus === 'confirmed_local' && !regionUnavailable && userCountry && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100" data-testid="avail-local">
              <MapPin className="h-2.5 w-2.5" /> Available in {userCountry}
            </span>
          )}
          {availStatus === 'potentially_global' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100" data-testid="avail-maybe-global">
              <Globe className="h-2.5 w-2.5" /> May vary by region
            </span>
          )}
          {regionUnavailable && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-500 border border-red-100" data-testid="region-unavailable">
              <MapPin className="h-2.5 w-2.5" /> {t('card.notInRegion')}
            </span>
          )}
          {!availStatus && !regionUnavailable && userCountry && !isGlobal && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-100" data-testid="region-available">
              <MapPin className="h-2.5 w-2.5" /> {t('card.availableIn')} {userCountry}
            </span>
          )}
          {hasLocalizedEarnings && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
              confidenceLevel === 'verified' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
              confidenceLevel === 'estimated' ? 'bg-amber-50 text-amber-600 border-amber-100' :
              'bg-slate-50 text-slate-500 border-slate-100'
            }`} data-testid="localized-earnings-badge">
              {localizedData.currency}{confidenceLevel && confidenceLevel !== 'verified' ? ` (${confidenceLevel})` : ''}
            </span>
          )}
          {hasApproxFromBackend && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-slate-50 text-slate-500 border-slate-100 cursor-help"
              title="Approximate conversion from USD. Actual rates vary by platform, location, demand, experience, and exchange-rate changes."
              data-testid="approx-earnings-currency-badge"
            >
              {opp.approx_earnings_currency} (approx.)
            </span>
          )}
          {isPricingOutdated && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100" data-testid="outdated-pricing-badge">
              <AlertTriangle className="h-2.5 w-2.5" /> Pricing may be outdated
            </span>
          )}
          {matchConfidence != null && matchConfidence >= 0.8 && (
            <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
              High confidence
            </span>
          )}
          {matchCategory && matchCategory !== 'General Match' && (
            <span className="text-[10px] text-slate-500 font-medium">{matchCategory}</span>
          )}
        </div>

        {/* === FOOTER ACTIONS === */}
        <div className="flex items-center justify-between pt-0">
          <button
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-teal-600 hover:text-teal-700 transition-colors group/btn"
            onClick={(e) => { e.stopPropagation(); sessionStorage.setItem('browse_scroll', window.scrollY.toString()); navigate(`/opportunity/${opp.opportunity_id}`); }}
            data-testid={`view-details-${opp.opportunity_id}`}
          >
            {t('card.viewDetails')}
            <ArrowRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-0.5" />
          </button>

          <div className="flex items-center gap-1">
            {/* Why this matches (for personalized) */}
            {isPersonalized && matchReasons.length > 2 && (
              <Button
                variant="ghost" size="sm"
                className="text-[11px] text-slate-400 hover:text-emerald-600 h-7 px-2"
                onClick={(e) => { e.stopPropagation(); setShowReasons(!showReasons); }}
                data-testid={`why-match-${opp.opportunity_id}`}
              >
                {showReasons ? 'Less' : 'Why?'}
              </Button>
            )}
            {/* Compare */}
            <Button
              variant="ghost" size="sm"
              className={`h-8 w-8 p-0 transition-all duration-200 ${inCompare ? 'text-teal-500 bg-teal-500/10 ring-1 ring-teal-500/20' : 'text-slate-300 hover:text-slate-500 hover:bg-slate-100'}`}
              onClick={(e) => {
                e.stopPropagation();
                if (inCompare) {
                  removeCompare(opp.opportunity_id);
                  toast.info(t('card.removedFromCompare'));
                } else {
                  addToCompare(opp);
                  toast.success(t('card.addedToCompare'));
                }
              }}
              data-testid={`compare-${opp.opportunity_id}`}
              aria-label={inCompare ? 'Remove from compare' : 'Add to compare'}
            >
              <BarChart3 className="h-3.5 w-3.5" />
            </Button>
            {/* Save / Unsave — always visible */}
            {onUnsave ? (
              <Button
                variant="ghost" size="sm"
                className="text-teal-600 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                onClick={(e) => { e.stopPropagation(); onUnsave(opp.opportunity_id); }}
                data-testid={`unsave-button-${opp.opportunity_id}`}
                aria-label="Remove saved"
              >
                <Star className="h-4 w-4 fill-current" />
              </Button>
            ) : onUnhide ? (
              <Button
                variant="ghost" size="sm"
                className="text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 h-8 w-8 p-0"
                onClick={(e) => { e.stopPropagation(); onUnhide(opp.opportunity_id); }}
                data-testid={`unhide-button-${opp.opportunity_id}`}
                aria-label="Unhide listing"
              >
                <EyeOff className="h-4 w-4" />
              </Button>
            ) : (
              <>
                {isLoggedIn && onHide && (
                  <Button
                    variant="ghost" size="sm"
                    className="text-slate-400 hover:text-orange-500 hover:bg-orange-50 h-8 w-8 p-0"
                    onClick={(e) => { e.stopPropagation(); onHide(opp.opportunity_id, opp.name); }}
                    data-testid={`hide-button-${opp.opportunity_id}`}
                    aria-label="Hide listing"
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                  </Button>
                )}
                <BookmarkButton opportunityId={opp.opportunity_id} size="sm" className="h-8 w-8 p-0" />
              </>
            )}
          </div>
        </div>

        {/* === EXPANDED REASONS (toggle) === */}
        {isPersonalized && showReasons && matchReasons.length > 2 && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid={`expanded-reasons-${opp.opportunity_id}`}>
            {matchReasons.slice(2).map((r, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[12px] text-slate-600">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>{r}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
